import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Smartphone, Monitor, Copy, Check, ExternalLink, Edit, Loader2, Mail, ChevronLeft, ChevronRight, Save, Undo2 } from 'lucide-react';
import { apiMethods } from '@/services/api';
import { themeList, getTheme } from '@/features/card/themes';
import CardView from '@/features/card/CardView';
import { cardFromUser } from '@/features/card/lib/fromUser';
import { rememberOwnCard } from '@/features/card/lib/device';

// Shows the owner exactly what visitors see: same component as the public card, inside a device frame.
const CardPreview = () => {
    const [device, setDevice] = useState('mobile');
    const [copied, setCopied] = useState(false);

    const { data, isLoading } = useQuery({
        queryKey: ['userProfile'],
        queryFn: async () => (await apiMethods.users.getProfile()).data,
    });
    const user = data?.user;
    const queryClient = useQueryClient();

    // Theme being tried on. Starts as the saved theme; only saved when the user taps Save.
    const savedTheme = getTheme(user?.cardTheme).id;
    const savedAccent = user?.cardAccentColor || null;
    const [draft, setDraft] = useState(null); // { theme, accent } or null = showing the saved one
    const themeId = draft?.theme ?? savedTheme;
    const accent = draft ? draft.accent : savedAccent;
    const theme = getTheme(themeId);
    const dirty = !!draft && (draft.theme !== savedTheme || (draft.accent || null) !== savedAccent);
    const index = Math.max(0, themeList.findIndex((t) => t.id === themeId));

    const pickTheme = (id) => setDraft({ theme: id, accent: id === savedTheme ? savedAccent : null });
    const step = (dir) => pickTheme(themeList[(index + dir + themeList.length) % themeList.length].id);

    const save = useMutation({
        mutationFn: () => apiMethods.users.updateProfile({ cardTheme: themeId, cardAccentColor: accent }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['userProfile'] });
            setDraft(null);
            toast.success(`${theme.label} is now live on your card`);
        },
        onError: (err) => toast.error(err?.response?.data?.message || 'Could not save the theme'),
    });

    // ← / → cycle themes (unless typing somewhere)
    useEffect(() => {
        const onKey = (e) => {
            if (e.altKey || e.ctrlKey || e.metaKey || /input|textarea|select/i.test(e.target.tagName)) return;
            if (e.key === 'ArrowLeft') step(-1);
            if (e.key === 'ArrowRight') step(1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });

    const card = cardFromUser(user, { theme: themeId, accentColor: accent });
    const cardUrl = user?.cardUuid ? `${window.location.origin}/card/${user.cardUuid}` : '';

    useEffect(() => {
        rememberOwnCard(user?.cardUuid);
    }, [user?.cardUuid]);

    const copy = async () => {
        await navigator.clipboard.writeText(cardUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
    };

    if (isLoading || !card) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
        );
    }

    const frame =
        device === 'mobile'
            ? { width: 390, height: 780, radius: 44, border: 12 }
            : { width: '100%', height: 820, radius: 16, border: 1 };

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Card Preview</h1>
                    <p className="text-sm text-gray-500">This is exactly what people see when they tap your HexLink. Try different themes below, then save the one you like.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
                        {[
                            ['mobile', Smartphone, 'Phone'],
                            ['desktop', Monitor, 'Desktop'],
                        ].map(([key, Icon, label]) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setDevice(key)}
                                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${
                                    device === key ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                <Icon className="h-4 w-4" />
                                {label}
                            </button>
                        ))}
                    </div>
                    <button type="button" onClick={copy} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                        {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                        {copied ? 'Copied' : 'Copy link'}
                    </button>
                    <a href={cardUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                        <ExternalLink className="h-4 w-4" />
                        Open live card
                    </a>
                    <Link to="/user/email-signature" className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                        <Mail className="h-4 w-4" />
                        Email signature
                    </Link>
                    <Link to="/user/profile" className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700">
                        <Edit className="h-4 w-4" />
                        Edit card
                    </Link>
                </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => step(-1)}
                        aria-label="Previous theme"
                        className="flex h-11 w-11 flex-none items-center justify-center rounded-full border border-gray-200 text-gray-700 hover:bg-gray-50"
                    >
                        <ChevronLeft className="h-5 w-5" />
                    </button>
                    <div className="min-w-0 flex-1 text-center">
                        <p className="truncate text-lg font-semibold text-gray-900">
                            {theme.label}
                            {themeId === savedTheme && !dirty && (
                                <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 align-middle text-xs font-medium text-green-700">Live</span>
                            )}
                        </p>
                        <p className="truncate text-sm text-gray-500">
                            {theme.description} · {index + 1} of {themeList.length}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => step(1)}
                        aria-label="Next theme"
                        className="flex h-11 w-11 flex-none items-center justify-center rounded-full border border-gray-200 text-gray-700 hover:bg-gray-50"
                    >
                        <ChevronRight className="h-5 w-5" />
                    </button>
                </div>

                <div role="radiogroup" aria-label="Card theme" className="-mx-1 mt-4 flex gap-2 overflow-x-auto px-1 pb-1">
                    {themeList.map((t) => {
                        const on = t.id === themeId;
                        return (
                            <button
                                key={t.id}
                                type="button"
                                role="radio"
                                aria-checked={on}
                                onClick={() => pickTheme(t.id)}
                                className={`inline-flex flex-none items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                                    on ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 text-gray-700 hover:border-gray-400'
                                }`}
                            >
                                <span
                                    className="h-3.5 w-3.5 rounded-full ring-1 ring-black/10"
                                    style={{ background: `linear-gradient(135deg, ${t.background} 50%, ${t.accents[0]} 50%)` }}
                                />
                                {t.label}
                                {t.id === savedTheme && <span className={`h-1.5 w-1.5 rounded-full ${on ? 'bg-green-400' : 'bg-green-500'}`} title="Live now" />}
                            </button>
                        );
                    })}
                </div>

                <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div role="radiogroup" aria-label="Accent color" className="flex flex-wrap items-center gap-2">
                        <span className="mr-1 text-sm text-gray-500">Accent</span>
                        {[null, ...theme.accents].map((hex) => {
                            const on = (accent || null) === hex || (hex && accent && accent.toLowerCase() === hex.toLowerCase());
                            const color = hex || theme.accents[0];
                            return (
                                <button
                                    key={hex || 'default'}
                                    type="button"
                                    role="radio"
                                    aria-checked={!!on}
                                    aria-label={hex ? `Accent ${hex}` : 'Theme default accent'}
                                    title={hex || 'Theme default'}
                                    onClick={() => setDraft({ theme: themeId, accent: hex })}
                                    className={`relative h-8 w-8 rounded-full ring-1 ring-black/10 transition ${on ? 'ring-2 ring-gray-900 ring-offset-2' : 'hover:scale-110'}`}
                                    style={{ background: color }}
                                >
                                    {!hex && <span className="absolute -bottom-1 -right-1 rounded bg-white px-1 text-[9px] font-semibold leading-tight text-gray-600 ring-1 ring-gray-200">AUTO</span>}
                                </button>
                            );
                        })}
                    </div>
                    <div className="flex gap-2">
                        {dirty && (
                            <button
                                type="button"
                                onClick={() => setDraft(null)}
                                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                <Undo2 className="h-4 w-4" />
                                Back to live
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => save.mutate()}
                            disabled={!dirty || save.isPending}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            {dirty ? `Use ${theme.label}` : 'Saved'}
                        </button>
                    </div>
                </div>
                <p className="mt-3 hidden text-xs text-gray-400 sm:block">Tip: use the ← → arrow keys to flip through themes.</p>
            </div>

            <div className="flex justify-center">
                <div
                    className="relative overflow-hidden bg-black shadow-2xl ring-1 ring-[#2a3644]"
                    style={{
                        width: frame.width,
                        maxWidth: '100%',
                        height: frame.height,
                        borderRadius: frame.radius,
                        border: `${frame.border}px solid #000`,
                    }}
                >
                    <div className="h-full overflow-y-auto" style={{ transform: 'translateZ(0)' }}>
                        <CardView card={card} mode="preview" isOwner />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CardPreview;
